import React, { useEffect, useRef, useState } from "react";
import FormItem from "@components/FormItem/FormItem";
import type {
  DryBulkResourceOwner,
  DryBulkResourceType,
} from "@services/dry-bulk-nomination/types";
import {
  DRY_BULK_NOMINATION_QUERY_KEY,
  getDryBulkBerthsByPort,
} from "@services/dryBulkNominationApi";
import { useQuery } from "@tanstack/react-query";
import { formatDateToDDMMYYYY } from "@utils/date-time";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  byLabel,
  codeLabel,
  labelFor,
  useDryBulkReferenceOptions,
} from "../hooks/useDryBulkReferenceOptions";
import {
  LIMITS,
  PORT_CODES,
  RESOURCE_TYPES,
  type CargoLine,
  type DryBulkNominationFormData,
  type ResourceFormData,
  type StoredActivity,
  type StoredAttachment,
} from "../schema/dryBulkNominationSchema";
import { formatUaeDateTime } from "../utils/formatUaeDate";
import ActivityDrawer from "./ActivityDrawer";
import CargoLineDrawer from "./CargoLineDrawer";
import DryBulkAttachmentsSection from "./DryBulkAttachmentsSection";
import DryBulkListSection from "./DryBulkListSection";
import ResourceDrawer from "./ResourceDrawer";

const LANG_BASE = "vessel-nomination.dry-bulk.add";
type SectionProps = { testId: string; readOnly?: boolean };
type ListName = "cargoLines" | "activities" | "resources" | "attachments";

/** Read / replace one list of the form; the form store persists every change (AC18). */
const useListField = <T,>(name: ListName) => {
  const { control, setValue, clearErrors } = useFormContext<DryBulkNominationFormData>();
  const items = (useWatch({ control, name }) ?? []) as T[];
  const replace = (next: T[]) => {
    clearErrors(name);
    setValue(name, next as never, { shouldDirty: true });
  };
  return {
    items,
    add: (item: T) => replace([...items, item]),
    update: (index: number, item: T) =>
      replace(items.map((current, i) => (i === index ? item : current))),
    remove: (index: number) => replace(items.filter((_, i) => i !== index)),
  };
};

/** Drawer state: `null` closed, `-1` adding, otherwise the index being edited. */
const useDrawer = () => {
  const [editing, setEditing] = useState<number | null>(null);
  return {
    editing,
    isOpen: editing !== null,
    openNew: () => setEditing(-1),
    openEdit: setEditing,
    close: () => setEditing(null),
  };
};

export const NominationSection: React.FC<SectionProps> = ({ testId, readOnly }) => {
  const { t } = useTranslation();
  const { portOptions, purposeOfVisitOptions } = useDryBulkReferenceOptions();
  const { control, setValue } = useFormContext<DryBulkNominationFormData>();
  const portCode = useWatch({ control, name: "portCode" });
  const berthCode = useWatch({ control, name: "expectedBerthCode" });
  const previousPort = useRef(portCode);
  const portCodeOptions = PORT_CODES.map((value) => ({
    value,
    label: t(`${LANG_BASE}.ports.${value}`),
  }));
  const { data: berths = [] } = useQuery({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, "berths-by-port", portCode],
    queryFn: () => getDryBulkBerthsByPort(portCode as string),
    enabled: !!portCode,
    staleTime: Infinity,
  });
  const berthOptions = berths
    .map((berth) => ({
      value: berth.berthCode,
      label: codeLabel(berth.berthCode, berth.berthName),
    }))
    .sort(byLabel);

  useEffect(() => {
    if (previousPort.current && previousPort.current !== portCode && !readOnly) {
      setValue("expectedBerthCode", "", { shouldDirty: true });
      setValue("expectedBerthName", "", { shouldDirty: true });
    }
    previousPort.current = portCode;
  }, [portCode, readOnly, setValue]);

  useEffect(() => {
    if (readOnly || !berthCode) return;
    const name = berths.find((berth) => berth.berthCode === berthCode)?.berthName;
    if (name) setValue("expectedBerthName", name);
  }, [berthCode, berths, readOnly, setValue]);

  return (
    <div className="flex w-full flex-col gap-6" data-testid={`${testId}-nomination`}>
      <div className="flex flex-col gap-1" data-testid={`${testId}-nomination-type`}>
        <span className="text-sm text-[#646A77]">{t(`${LANG_BASE}.fields.nomination`)}</span>
        <span className="font-semibold">{t(`${LANG_BASE}.fields.nomination-type-value`)}</span>
      </div>
      <div className="grid w-full grid-cols-1 gap-x-6 md:grid-cols-2">
        <FormItem<DryBulkNominationFormData, "portCode">
          testId={`${testId}-port`}
          name="portCode"
          type="select"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.port`)}
          options={portCodeOptions}
        />
        <div className="hidden md:block" />
        <FormItem<DryBulkNominationFormData, "lastPortCode">
          testId={`${testId}-last-port`}
          name="lastPortCode"
          type="autocomplete"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.last-port`)}
          options={portOptions}
        />
        <FormItem<DryBulkNominationFormData, "nextPortCode">
          testId={`${testId}-next-port`}
          name="nextPortCode"
          type="autocomplete"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.next-port`)}
          options={portOptions}
        />
        <FormItem<DryBulkNominationFormData, "purposeOfVisitCode">
          testId={`${testId}-purpose-of-visit`}
          name="purposeOfVisitCode"
          type="autocomplete"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.purpose-of-visit`)}
          options={purposeOfVisitOptions}
        />
        <div className="hidden md:block" />
        <FormItem<DryBulkNominationFormData, "expectedBerthCode">
          testId={`${testId}-expected-berth`}
          name="expectedBerthCode"
          type="autocomplete"
          required
          disabled={readOnly || !portCode}
          label={t(`${LANG_BASE}.fields.expected-berth`)}
          options={berthOptions}
        />
        <FormItem<DryBulkNominationFormData, "expectedBerthName">
          testId={`${testId}-berth-name`}
          name="expectedBerthName"
          disabled
          label={t(`${LANG_BASE}.fields.berth-name`)}
        />
        <FormItem<DryBulkNominationFormData, "viaNumber">
          testId={`${testId}-via-number`}
          name="viaNumber"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.via-number`)}
          maxChars={LIMITS.VIA_NUMBER}
        />
        <div className="hidden md:block" />
        <FormItem<DryBulkNominationFormData, "initialEta">
          testId={`${testId}-initial-eta`}
          name="initialEta"
          type="date-picker"
          timePicker
          combinedInput
          dateConstraint="not-past"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.initial-eta`)}
        />
        <FormItem<DryBulkNominationFormData, "maxArrivalDraft">
          testId={`${testId}-max-arrival-draft`}
          name="maxArrivalDraft"
          type="number"
          disabled={readOnly}
          label={t(`${LANG_BASE}.fields.max-arrival-draft`)}
          maxDigits={LIMITS.MAX_ARRIVAL_DRAFT_DIGITS}
          decimals={0}
        />
      </div>
    </div>
  );
};

export const CargoLinesSection: React.FC<SectionProps> = ({ testId, readOnly }) => {
  const { t } = useTranslation();
  const { cargoOptions, companyOptions, shipperOptions, receiverOptions, stockPileOptions } =
    useDryBulkReferenceOptions();
  const lines = useListField<CargoLine>("cargoLines");
  const drawer = useDrawer();
  const L = `${LANG_BASE}.cargo-lines`;

  const rows = lines.items.map((line, index) => ({
    id: index + 1,
    index,
    sequence: index + 1,
    operationType: t(`${L}.operation-types.${line.operationType}`),
    cargoCode: labelFor(cargoOptions, line.cargoCode),
    quantityMt: line.quantityMt,
    party:
      line.operationType === "LOADING"
        ? `${labelFor(companyOptions, line.shipperCompanyCode)} / ${labelFor(stockPileOptions, line.stockPileCode)}`
        : labelFor(companyOptions, line.receiverCompanyCode),
  }));

  return (
    <DryBulkListSection
      testId={`${testId}-cargo-lines`}
      title={t(`${LANG_BASE}.sections.cargo-lines`)}
      addLabel={t(`${L}.add-button`)}
      headers={[
        { field: "sequence", columnName: t(`${L}.headers.id`) },
        { field: "operationType", columnName: t(`${L}.headers.operation-type`) },
        { field: "cargoCode", columnName: t(`${L}.headers.cargo-code`) },
        { field: "quantityMt", columnName: t(`${L}.headers.quantity`) },
        { field: "party", columnName: t(`${L}.headers.party`) },
        { field: "action", columnName: t(`${L}.headers.action`) },
      ]}
      rows={rows}
      readOnly={readOnly}
      onAdd={drawer.openNew}
      onEdit={drawer.openEdit}
      onDelete={lines.remove}
    >
      <CargoLineDrawer
        isOpen={drawer.isOpen}
        line={
          drawer.editing !== null && drawer.editing >= 0 ? lines.items[drawer.editing] : undefined
        }
        cargoOptions={cargoOptions}
        shipperOptions={shipperOptions}
        receiverOptions={receiverOptions}
        stockPileOptions={stockPileOptions}
        onClose={drawer.close}
        onSave={(line) => {
          if (drawer.editing !== null && drawer.editing >= 0) lines.update(drawer.editing, line);
          else lines.add(line);
          drawer.close();
        }}
      />
    </DryBulkListSection>
  );
};

const formatDateTime = (iso?: string | null) => (iso ? formatUaeDateTime(iso) : "—");
const formatDate = (iso?: string | null) => (iso ? formatDateToDDMMYYYY(iso) : "—");

export const ActivitiesSection: React.FC<SectionProps> = ({ testId, readOnly }) => {
  const { t } = useTranslation();
  const activities = useListField<StoredActivity>("activities");
  const {
    formState: { errors },
  } = useFormContext<DryBulkNominationFormData>();
  const drawer = useDrawer();
  const L = `${LANG_BASE}.activities`;

  const rows = activities.items.map((activity, index) => ({
    id: index + 1,
    index,
    sequence: index + 1,
    activityType:
      activity.activityType === "OTHER" && activity.otherText
        ? `${t(`${L}.types.OTHER`)} – ${activity.otherText}`
        : t(`${L}.types.${activity.activityType}`),
    dateFrom: formatDateTime(activity.dateFrom),
    dateTo: formatDateTime(activity.dateTo),
  }));

  return (
    <DryBulkListSection
      testId={`${testId}-activities`}
      title={t(`${LANG_BASE}.sections.activities`)}
      error={errors.activities?.message ?? errors.activities?.root?.message}
      addLabel={t(`${L}.add-button`)}
      headers={[
        { field: "sequence", columnName: t(`${L}.headers.id`) },
        { field: "activityType", columnName: t(`${L}.headers.type`) },
        { field: "dateFrom", columnName: t(`${L}.headers.date-from`) },
        { field: "dateTo", columnName: t(`${L}.headers.date-to`) },
        { field: "action", columnName: t(`${L}.headers.action`) },
      ]}
      rows={rows}
      readOnly={readOnly}
      onAdd={drawer.openNew}
      onEdit={drawer.openEdit}
      onDelete={activities.remove}
    >
      <ActivityDrawer
        isOpen={drawer.isOpen}
        activity={
          drawer.editing !== null && drawer.editing >= 0
            ? activities.items[drawer.editing]
            : undefined
        }
        onClose={drawer.close}
        onSave={(activity) => {
          if (drawer.editing !== null && drawer.editing >= 0)
            activities.update(drawer.editing, activity);
          else activities.add(activity);
          drawer.close();
        }}
      />
    </DryBulkListSection>
  );
};

/** DBVN-0010: the reviewer edits the saved list directly (every change is one PUT), not the form. */
export interface ControlledResources {
  items: ResourceFormData[];
  onChange: (next: ResourceFormData[]) => void;
  busy?: boolean;
}

export const ResourcesSection: React.FC<
  SectionProps & { owner: DryBulkResourceOwner; controlled?: ControlledResources }
> = ({ testId, owner, readOnly, controlled }) => {
  const { t } = useTranslation();
  const formResources = useListField<ResourceFormData>("resources");
  const resources = controlled
    ? {
        items: controlled.items,
        add: (item: ResourceFormData) => controlled.onChange([...controlled.items, item]),
        update: (index: number, item: ResourceFormData) =>
          controlled.onChange(controlled.items.map((current, i) => (i === index ? item : current))),
        remove: (index: number) =>
          controlled.onChange(controlled.items.filter((_, i) => i !== index)),
      }
    : formResources;
  const drawer = useDrawer();
  const L = `${LANG_BASE}.resources`;
  const sectionKey = owner === "VESSEL" ? "vessel-resources" : "od-resources";

  const own = resources.items
    .map((resource, index) => ({ resource, index }))
    .filter(({ resource }) => resource.owner === owner);
  const usedTypes = own.map(({ resource }) => resource.type);
  const availableTypes = RESOURCE_TYPES.filter(
    (type) => !usedTypes.includes(type),
  ) as DryBulkResourceType[];

  const EMPTY_CELL = "-";
  const numberCell = (value: unknown) =>
    value === undefined || value === null || value === "" ? EMPTY_CELL : String(value);
  const conditionLabel = (good?: boolean | null) =>
    good === undefined || good === null
      ? EMPTY_CELL
      : t(`${L}.condition.${good ? "good" : "not-good"}`);
  const workingCondition = (resource: ResourceFormData) =>
    resource.cranesGoodCondition == null && resource.grabsGoodCondition == null
      ? EMPTY_CELL
      : `${conditionLabel(resource.cranesGoodCondition)} / ${conditionLabel(resource.grabsGoodCondition)}`;

  const rows = own.map(({ resource, index }, position) => {
    const geared = resource.type !== "GEARLESS";
    return {
      id: position + 1,
      index,
      type: t(`${L}.types.${resource.type}`),
      count: numberCell(geared ? resource.craneCount : resource.resourceCount),
      craneCapacityMt: geared ? numberCell(resource.craneCapacityMt) : EMPTY_CELL,
      craneSwl: geared ? numberCell(resource.craneSwl) : EMPTY_CELL,
      grabCount: geared ? numberCell(resource.grabCount) : EMPTY_CELL,
      grabSizeCbm: geared ? numberCell(resource.grabSizeCbm) : EMPTY_CELL,
      grabSwl: geared ? numberCell(resource.grabSwl) : EMPTY_CELL,
      condition: geared ? workingCondition(resource) : EMPTY_CELL,
    };
  });

  return (
    <DryBulkListSection
      testId={`${testId}-${sectionKey}`}
      title={t(`${LANG_BASE}.sections.${sectionKey}`)}
      addLabel={t(`${L}.add-button`)}
      headers={[
        { field: "type", columnName: t(`${L}.headers.type`) },
        { field: "count", columnName: t(`${L}.headers.count`) },
        { field: "craneCapacityMt", columnName: t(`${L}.headers.crane-capacity`) },
        { field: "craneSwl", columnName: t(`${L}.headers.crane-swl`) },
        { field: "grabCount", columnName: t(`${L}.headers.grab-count`) },
        { field: "grabSizeCbm", columnName: t(`${L}.headers.grab-size`) },
        { field: "grabSwl", columnName: t(`${L}.headers.grab-swl`) },
        { field: "condition", columnName: t(`${L}.headers.working-condition`) },
        { field: "action", columnName: t(`${L}.headers.action`) },
      ]}
      rows={rows}
      canAdd={availableTypes.length > 0 && !controlled?.busy}
      readOnly={readOnly}
      onAdd={drawer.openNew}
      onEdit={drawer.openEdit}
      onDelete={resources.remove}
    >
      <ResourceDrawer
        isOpen={drawer.isOpen}
        owner={owner}
        resource={
          drawer.editing !== null && drawer.editing >= 0
            ? resources.items[drawer.editing]
            : undefined
        }
        availableTypes={availableTypes}
        onClose={drawer.close}
        onSave={(resource) => {
          if (drawer.editing !== null && drawer.editing >= 0)
            resources.update(drawer.editing, resource);
          else resources.add(resource);
          drawer.close();
        }}
      />
    </DryBulkListSection>
  );
};

export const AttachmentsSection: React.FC<SectionProps> = ({ testId, readOnly }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<DryBulkNominationFormData>();
  const attachments = (useWatch({ control, name: "attachments" }) ?? []) as StoredAttachment[];
  const L = `${LANG_BASE}.attachments`;

  if (!readOnly) return <DryBulkAttachmentsSection testId={testId} />;

  const rows = attachments.map((attachment, index) => ({
    id: index + 1,
    index,
    documentType:
      attachment.documentType === "OTHERS" && attachment.otherSpecify
        ? `${t(`${L}.types.OTHERS`)} – ${attachment.otherSpecify}`
        : t(`${L}.types.${attachment.documentType}`),
    documentName: attachment.documentName || "—",
    issueDate: formatDate(attachment.issueDate),
    expiryDate: formatDate(attachment.expiryDate),
  }));

  return (
    <DryBulkListSection
      testId={`${testId}-attachments`}
      title={t(`${LANG_BASE}.sections.attachments`)}
      addLabel={t(`${L}.add`)}
      headers={[
        { field: "documentType", columnName: t(`${L}.headers.type`) },
        { field: "documentName", columnName: t(`${L}.headers.name`) },
        { field: "issueDate", columnName: t(`${L}.headers.issue-date`) },
        { field: "expiryDate", columnName: t(`${L}.headers.expiry-date`) },
      ]}
      rows={rows}
      readOnly
      onAdd={() => undefined}
      onEdit={() => undefined}
      onDelete={() => undefined}
    />
  );
};

export const RemarksSection: React.FC<SectionProps> = ({ testId, readOnly }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full" data-testid={`${testId}-remarks`}>
      <FormItem<DryBulkNominationFormData, "remarks">
        testId={`${testId}-remarks-input`}
        name="remarks"
        disabled={readOnly}
        label={t(`${LANG_BASE}.fields.remarks`)}
        maxChars={LIMITS.REMARKS}
      />
    </div>
  );
};
